import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { courseModules } from '../data/courseData';
import { useAuth } from './AuthContext';

const ProgressContext = createContext();
const STORAGE_KEY = 'digital_safety_progress';
const TOTAL_MODULES = 12;

const emptyProgress = () => ({
  completedModules: [],
  quizResults: {},
  checklistItems: {},
  fillInFields: {},
  notes: {},
  currentModule: 1,
  startedAt: new Date().toISOString(),
  completedAt: null,
});

function sanitiseProgress(parsed) {
  if (!parsed || typeof parsed !== 'object') return emptyProgress();
  return {
    completedModules: Array.isArray(parsed.completedModules) ? parsed.completedModules : [],
    quizResults: typeof parsed.quizResults === 'object' && parsed.quizResults !== null ? parsed.quizResults : {},
    checklistItems: typeof parsed.checklistItems === 'object' && parsed.checklistItems !== null ? parsed.checklistItems : {},
    fillInFields: typeof parsed.fillInFields === 'object' && parsed.fillInFields !== null ? parsed.fillInFields : {},
    notes: typeof parsed.notes === 'object' && parsed.notes !== null ? parsed.notes : {},
    currentModule: typeof parsed.currentModule === 'number' && parsed.currentModule >= 1 ? parsed.currentModule : 1,
    startedAt: parsed.startedAt || new Date().toISOString(),
    completedAt: parsed.completedAt || null,
  };
}

function loadLocalProgress() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      // Trust only client-editable fields from device storage;
      // completedModules + quizResults are server-authoritative.
      const parsed = JSON.parse(saved);
      return sanitiseProgress({
        ...parsed,
        completedModules: [],
        quizResults: {},
      });
    }
  } catch (e) {
    console.error('Error loading local progress', e);
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  }
  return emptyProgress();
}

export function ProgressProvider({ children }) {
  const { isAuthenticated, token, authFetch, user } = useAuth();
  const [progress, setProgress] = useState(loadLocalProgress);
  const [isSynced, setIsSynced] = useState(false);
  const [saveState, setSaveState] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const saveTimer = useRef(null);
  const savedResetTimer = useRef(null);
  const hasHydrated = useRef(false);

  const refreshFromServer = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const resp = await authFetch('/api/user/progress');
      if (resp.ok) {
        const data = await resp.json();
        setProgress(sanitiseProgress(data.progress));
      }
    } catch (e) {
      console.error('refreshFromServer error', e);
    }
  }, [isAuthenticated, authFetch]);

  // Hydrate: fetch from backend if logged in; otherwise reload localStorage
  useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      if (!isAuthenticated) {
        hasHydrated.current = true;
        setProgress(loadLocalProgress());
        setIsSynced(false);
        return;
      }
      try {
        const resp = await authFetch('/api/user/progress');
        if (resp.ok) {
          const data = await resp.json();
          if (!cancelled) {
            const serverProg = sanitiseProgress(data.progress);
            const local = loadLocalProgress();
            const hasLocalData = Object.keys(local.notes || {}).length > 0
              || Object.keys(local.checklistItems).length > 0
              || Object.keys(local.fillInFields).length > 0
              || local.currentModule > 1;
            const serverIsEmpty = Object.keys(serverProg.checklistItems).length === 0
              && Object.keys(serverProg.notes || {}).length === 0
              && Object.keys(serverProg.fillInFields).length === 0
              && serverProg.currentModule <= 1;
            if (hasLocalData && serverIsEmpty) {
              const migrateResp = await authFetch('/api/user/migrate-local', {
                method: 'POST',
                body: JSON.stringify({
                  checklistItems: local.checklistItems,
                  notes: local.notes || {},
                  fillInFields: local.fillInFields,
                  currentModule: local.currentModule,
                }),
              });
              if (migrateResp.ok) {
                try { localStorage.removeItem(STORAGE_KEY); } catch {}
                const r2 = await authFetch('/api/user/progress');
                if (r2.ok) {
                  const d2 = await r2.json();
                  if (!cancelled) setProgress(sanitiseProgress(d2.progress));
                }
              } else {
                setProgress(serverProg);
              }
            } else {
              setProgress(serverProg);
            }
            setIsSynced(true);
            hasHydrated.current = true;
          }
        }
      } catch (e) {
        console.error('Progress hydrate error', e);
        hasHydrated.current = true;
      }
    };
    hydrate();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token, user?.id]);

  // Debounced save for user-editable fields only
  useEffect(() => {
    if (!hasHydrated.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (savedResetTimer.current) clearTimeout(savedResetTimer.current);
    setSaveState('saving');
    saveTimer.current = setTimeout(async () => {
      if (isAuthenticated) {
        try {
          // Only send user-editable fields; completedModules/quizResults are server-managed
          const payload = {
            completedModules: [],
            quizResults: {},
            checklistItems: progress.checklistItems,
            fillInFields: progress.fillInFields,
            notes: progress.notes,
            currentModule: progress.currentModule,
            startedAt: progress.startedAt,
          };
          const r = await authFetch('/api/user/progress', { method: 'PUT', body: JSON.stringify(payload) });
          setSaveState(r.ok ? 'saved' : 'error');
        } catch { setSaveState('error'); }
      } else {
        try {
          // Device-only save (no completedModules/quizResults — those are earned on server)
          const safe = { ...progress, completedModules: [], quizResults: {} };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
          setSaveState('saved');
        } catch { setSaveState('error'); }
      }
      savedResetTimer.current = setTimeout(() => setSaveState('idle'), 2000);
    }, 400);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      if (savedResetTimer.current) clearTimeout(savedResetTimer.current);
    };
  }, [progress, isAuthenticated, authFetch]);

  const toggleChecklistItem = (moduleId, itemId) => {
    setProgress((prev) => {
      const mc = prev.checklistItems[moduleId] || {};
      return { ...prev, checklistItems: { ...prev.checklistItems, [moduleId]: { ...mc, [itemId]: !mc[itemId] } } };
    });
  };

  const saveFillInField = (moduleId, fieldId, value) => {
    setProgress((prev) => ({
      ...prev,
      fillInFields: { ...prev.fillInFields, [moduleId]: { ...(prev.fillInFields[moduleId] || {}), [fieldId]: value } },
    }));
  };

  const saveNote = (moduleId, value) => {
    setProgress((prev) => ({ ...prev, notes: { ...prev.notes, [moduleId]: value } }));
  };

  const setCurrentModule = (moduleId) => {
    setProgress((prev) => (prev.currentModule === moduleId ? prev : { ...prev, currentModule: moduleId }));
  };

  const getModuleProgress = (moduleId) => {
    const module = courseModules.find((m) => m.id === moduleId);
    if (!module) return 0;
    // Server-authoritative quiz completion
    if (progress.quizResults?.[String(moduleId)]?.passed || progress.quizResults?.[moduleId]?.passed) return 100;
    return 0;
  };

  const getOverallProgress = () => {
    return Math.round(((progress.completedModules?.length || 0) / TOTAL_MODULES) * 100);
  };

  const resetProgress = useCallback(async () => {
    if (isAuthenticated) {
      try {
        await authFetch('/api/user/reset-progress', { method: 'POST' });
      } catch (e) {
        console.error('reset-progress error', e);
      }
      await refreshFromServer();
    } else {
      setProgress(emptyProgress());
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
    }
  }, [isAuthenticated, authFetch, refreshFromServer]);

  const isModuleComplete = (moduleId) => (progress.completedModules || []).includes(moduleId);
  const isQuizPassed = (moduleId) =>
    progress.quizResults?.[String(moduleId)]?.passed === true ||
    progress.quizResults?.[moduleId]?.passed === true;

  return (
    <ProgressContext.Provider value={{
      progress,
      isSynced,
      saveState,
      refreshFromServer,
      toggleChecklistItem,
      saveFillInField,
      saveNote,
      setCurrentModule,
      getModuleProgress,
      getOverallProgress,
      resetProgress,
      isModuleComplete,
      isQuizPassed,
    }}>
      {children}
    </ProgressContext.Provider>
  );
}

export function useProgress() {
  const context = useContext(ProgressContext);
  if (!context) throw new Error('useProgress must be used within a ProgressProvider');
  return context;
}
